import type { GetListResponse } from "libs/api";

// GET admin/ai-review: the WhatsApp AI replies the sweep found suspicious
// (backend contract §19.7). The sweep runs without any model.

export enum EAiReviewStatus {
  Open = "open",
  Reviewed = "reviewed",
  Dismissed = "dismissed"
}

export type AiReviewFlag =
  | "F1"
  | "F2"
  | "F3"
  | "F4"
  | "F5"
  | "F6"
  | "F7"
  | "F8"
  | "F9";

export interface AiReviewReason {
  code: AiReviewFlag;
  // The weight when the reason was last merged (the constants may change).
  weight: number;
  // Closed-list sub-codes (AI_REVIEW_DETAILS); any other value is ignored.
  details: string[];
  // The turn whose messages showed it: the item's own turn, or the customer's
  // next message (a correction or a repeated message).
  seenInTurnMessageId: string;
}

export interface AiReviewLessonRef {
  id: string;
  title: string;
  status: string;
}

export interface AiReviewItem {
  id: string;
  channel: "whatsapp";
  conversationId: string;
  // The customer message the reply answered (the turn's trigger).
  turnMessageId: string;
  turnAt: string;
  reasons: AiReviewReason[];
  score: number;
  status: EAiReviewStatus;
  reviewedByAdminId: string | null;
  reviewedAt: string | null;
  lessonId: string | null;
  createdAt: string;
  updatedAt: string;
  // Read live when the list is built, never stored on the item.
  // The trigger's messageType; null when the row is gone.
  inboundType: string | null;
  // The customer's words, cut; null for a voice note, a photo, etc.
  inboundExcerpt: string | null;
  // The turn's first AI reply text, cut; null when there is none.
  replyExcerpt: string | null;
  lesson: AiReviewLessonRef | null;
  // An AI ticket already opened on the conversation for this reply (from the
  // customer message up to 30 min later): "Ticket already open". Absent from
  // a backend that predates it.
  ticket?: AiReviewTicketRef | null;
}

export interface AiReviewTicketRef {
  id: string;
  // The support ticket status: open, in_progress, waiting_for_customer,
  // resolved or closed.
  status: string;
}

export interface AiReviewCounts {
  open: number;
  reviewed: number;
  dismissed: number;
}

// The sweep's schedule, so the page text follows the backend constants.
export interface AiReviewSweepInfo {
  enabled: boolean;
  everyMinutes: number;
  delayMinutes: number;
  lastRunAt: string | null;
}

export type AiReviewListResponse = GetListResponse<AiReviewItem> & {
  // Every item, whatever the tab.
  counts: AiReviewCounts;
  sweep: AiReviewSweepInfo;
};
